import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Award, MapPin, ArrowRight, Users, BookOpen } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import FloatingActionButtons from '@/components/shared/FloatingActionButtons';
import Seo from '@/components/shared/Seo';
import { Button } from '@/components/ui/button';
import { SEO_PAGES } from '@/lib/seoPageRegistry';

/**
 * Renders a single suburb × service SEO landing page.
 * All variability (title, copy, nearby suburbs) is sourced from the
 * registry entry passed in as `page`.
 */
const SuburbServicePage = ({ page }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { service, suburb } = page;

  // Other suburb pages for the SAME service — clean, topical internal links.
  const relatedSuburbs = SEO_PAGES.filter(
    (p) => p.service.key === service.key && p.suburb.slug !== suburb.slug
  );

  // JSON-LD LocalBusiness schema specifically scoped to this suburb —
  // boosts chances of appearing in local search results.
  const localSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `${service.name} in ${suburb.name}`,
    description: page.description,
    provider: {
      '@type': 'EducationalOrganization',
      name: 'Winhouse Tutors',
      url: 'https://winhousetutors.academy',
    },
    areaServed: {
      '@type': 'City',
      name: suburb.name,
      address: {
        '@type': 'PostalAddress',
        addressLocality: suburb.name,
        postalCode: suburb.postcode,
        addressRegion: 'VIC',
        addressCountry: 'AU',
      },
    },
    serviceType: service.name,
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={page.title}
        description={page.description}
        path={page.path}
        jsonLd={localSchema}
      />

      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />

      {/* Hero */}
      <section className="border-b" style={{ paddingTop: '6rem', borderBottomColor: 'rgba(183, 191, 114, 0.20)', background: 'linear-gradient(180deg, #FFFFFF 0%, #FAF9F6 100%)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 mb-5">
            <MapPin className="h-3.5 w-3.5" />
            {suburb.name} · {suburb.postcode}
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight text-gray-900"
            data-testid={`suburb-page-h1-${service.key}-${suburb.slug}`}
          >
            {service.name} in {suburb.name} – Expert Tutoring with Proven Results
          </h1>
          <p className="mt-6 text-lg text-gray-700 max-w-3xl">
            {suburb.hook}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/get-started" data-testid={`suburb-cta-primary-${service.key}-${suburb.slug}`}>
              <Button className="btn-teal">
                {service.ctaHeadline} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/tutors">
              <Button variant="outline" className="border-gray-300">
                Browse Our Tutors
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Intro paragraph — unique per suburb */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-700 leading-relaxed text-lg">
            At <strong>Winhouse Tutors</strong>, our {service.label} programme
            for <strong>{suburb.name}</strong> is built around the reality that
            local students face. The area is {suburb.highlight}, and our tutors
            understand exactly what schools in the catchment expect. Whether
            you live on a quiet {suburb.name} street or commute in from
            {' '}{suburb.nearby[0]} or {suburb.nearby[1]}, our {service.label}{' '}
            coaching meets you where you are and builds a clear path to the
            result you want.
          </p>
          <p className="text-gray-700 leading-relaxed text-lg mt-4">
            Every {service.label.toLowerCase()} student in {suburb.name} starts
            with a proper diagnostic. We identify the exact gaps, set a
            realistic target, and work backwards from it — no fluffy
            &ldquo;general tutoring&rdquo;, no generic worksheets. Just focused
            one-on-one sessions that are measured against concrete milestones.
          </p>
        </div>
      </section>

      {/* Why choose us */}
      <section className="bg-[#FAF9F6] border-y" style={{ borderColor: 'rgba(183, 191, 114, 0.20)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Award className="h-7 w-7 text-[#007A7A]" />
            Why Choose Winhouse Tutors for {service.label} in {suburb.name}
          </h2>
          <ul className="mt-8 grid md:grid-cols-2 gap-4">
            {service.why.map((reason, idx) => (
              <li
                key={idx}
                className="flex gap-3 items-start p-4 bg-white rounded-lg border border-gray-100"
              >
                <CheckCircle2 className="h-5 w-5 text-[#007A7A] shrink-0 mt-0.5" />
                <span className="text-gray-700">{reason}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* What you get */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <BookOpen className="h-7 w-7 text-[#007A7A]" />
          What&apos;s Included in Your {service.label} Programme
        </h2>
        <p className="mt-3 text-gray-600">
          Every {service.label} student in {suburb.name} receives the same
          structured programme, adapted to their diagnostic result and
          individual goals:
        </p>
        <div className="mt-6 space-y-3">
          {service.whatYouGet.map((item, idx) => (
            <div key={idx} className="flex gap-3 items-start">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#D3D86F]/40 text-[#007A7A] font-semibold flex items-center justify-center text-sm">
                {idx + 1}
              </span>
              <span className="text-gray-700 pt-0.5">{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Who this is for */}
      <section className="bg-[#FAF9F6] border-y" style={{ borderColor: 'rgba(183, 191, 114, 0.20)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="h-7 w-7 text-[#007A7A]" />
            Who Our {suburb.name} {service.label} Programme Is For
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {service.whoFor.map((audience, idx) => (
              <div
                key={idx}
                className="p-5 rounded-lg bg-white border border-gray-100"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#007A7A] shrink-0 mt-0.5" />
                  <p className="text-gray-700">{audience}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local area */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <MapPin className="h-7 w-7 text-[#007A7A]" />
          Serving {suburb.name} and the Surrounding Area
        </h2>
        <p className="mt-4 text-gray-700 leading-relaxed">
          Our {service.label.toLowerCase()} tutors travel throughout{' '}
          {suburb.name} <strong>{suburb.postcode}</strong> and the adjoining
          suburbs, including{' '}
          {suburb.nearby.slice(0, -1).map((s, i) => (
            <span key={s}>
              <strong>{s}</strong>{i < suburb.nearby.length - 2 ? ', ' : ''}
            </span>
          ))}
          {' '}and <strong>{suburb.nearby[suburb.nearby.length - 1]}</strong>.
          Sessions run either online (students log in from anywhere in
          Australia) or in-person at a mutually convenient location —
          typically a local library, a quiet café, or your home.
        </p>
        <p className="mt-4 text-gray-700 leading-relaxed">
          If you prefer fully online {service.label.toLowerCase()} lessons, our
          video-based sessions mirror the exact same curriculum and feedback
          structure you&apos;d get in person — just without the commute.
          That&apos;s often the best fit for families balancing{' '}
          {suburb.name}&apos;s busy weeknight schedules.
        </p>
        <p className="mt-4 text-gray-700 leading-relaxed">
          Because our {service.label} specialists are drawn from across
          Melbourne, you&apos;re not limited to the handful of tutors who
          happen to live in {suburb.name}. We match you to the best tutor for
          <em> your</em> target outcome — whether that means a specialist in
          band 8 {service.label === 'IELTS' ? 'writing' : 'exam technique'}, a
          methods teacher who walks you through every SAC topic, or a primary
          specialist who makes weekly progress visible to parents.
        </p>
      </section>

      {/* How sessions work */}
      <section className="bg-[#FAF9F6] border-y" style={{ borderColor: 'rgba(183, 191, 114, 0.20)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <h2 className="text-3xl font-bold text-gray-900">
            How {service.label} Sessions Work in {suburb.name}
          </h2>
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-white rounded-lg border border-gray-100">
              <div className="text-xs uppercase tracking-widest text-[#007A7A] mb-2">
                Week 1
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">
                Diagnostic &amp; Plan
              </h3>
              <p className="text-sm text-gray-600">
                Your tutor assesses current level, maps strengths and gaps,
                and builds a week-by-week {service.label.toLowerCase()} plan
                aligned with your school in {suburb.name} or your target
                score.
              </p>
            </div>
            <div className="p-6 bg-white rounded-lg border border-gray-100">
              <div className="text-xs uppercase tracking-widest text-[#007A7A] mb-2">
                Weeks 2–6
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">
                Targeted Coaching
              </h3>
              <p className="text-sm text-gray-600">
                Weekly 1-on-1 sessions close the highest-impact gaps first.
                Between sessions you get practice work, review feedback, and
                unlimited email support — all measured against the plan.
              </p>
            </div>
            <div className="p-6 bg-white rounded-lg border border-gray-100">
              <div className="text-xs uppercase tracking-widest text-[#007A7A] mb-2">
                Weeks 7+
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">
                Exam Readiness
              </h3>
              <p className="text-sm text-gray-600">
                Timed practice under exam conditions, SAC or mock-test
                simulation, and a final confidence-builder in the two weeks
                before your {service.label} assessment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — SEO rich snippet opportunity */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="text-3xl font-bold text-gray-900">
          {service.label} Tutoring in {suburb.name} — FAQs
        </h2>
        <div className="mt-8 space-y-5">
          <div>
            <h3 className="font-semibold text-gray-900">
              How much does {service.label.toLowerCase()} tutoring in {suburb.name} cost?
            </h3>
            <p className="mt-2 text-gray-700">
              1-on-1 sessions are AUD 55 per 60-minute lesson — same price for every tutor.
              Or save with our Intensive Package: AUD 250 per month for 4 sessions, including
              unlimited writing feedback, priority scheduling, and mock test assessments.
              Transparent pricing — no surprises.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              Do you offer in-person {service.label.toLowerCase()} lessons in {suburb.name}?
            </h3>
            <p className="mt-2 text-gray-700">
              Yes. A portion of our tutors travel to {suburb.name} and
              neighbouring suburbs like {suburb.nearby[0]} and{' '}
              {suburb.nearby[1]} for in-home or library-based lessons. Most
              families, however, choose our fully-online format once they see
              how effective modern video coaching can be.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              How soon can I start {service.label.toLowerCase()} tutoring?
            </h3>
            <p className="mt-2 text-gray-700">
              Most students are matched to a tutor within 48 hours of
              completing the free diagnostic, and the first full session
              usually starts the following week.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              Will my tutor know the school I attend in {suburb.name}?
            </h3>
            <p className="mt-2 text-gray-700">
              We match you to a tutor who has tutored students from schools in
              the {suburb.name} catchment where possible, which means they
              already understand your specific assessment style — particularly
              useful for SAC preparation and exam technique.
            </p>
          </div>
        </div>
      </section>

      {/* Related Suburbs — topical internal linking within the same service */}
      {relatedSuburbs.length > 0 && (
        <section
          className="bg-[#FAF9F6] border-y"
          style={{ borderColor: 'rgba(183, 191, 114, 0.20)' }}
          data-testid={`related-suburbs-${service.key}-${suburb.slug}`}
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <h2 className="text-3xl font-bold text-gray-900">
              {service.label} Tutoring in Nearby Suburbs
            </h2>
            <p className="mt-3 text-gray-600 max-w-2xl">
              Not quite in {suburb.name}? We offer the same {service.label}{' '}
              programme across surrounding Melbourne suburbs. Explore a nearby
              location:
            </p>
            <div className="mt-8 grid sm:grid-cols-2 md:grid-cols-4 gap-4">
              {relatedSuburbs.map((rp) => (
                <Link
                  key={rp.path}
                  to={rp.path}
                  data-testid={`related-suburb-link-${service.key}-${rp.suburb.slug}`}
                  className="group p-5 rounded-lg bg-white border border-gray-100 hover:border-[#007A7A] hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 mb-2">
                    <MapPin className="h-3 w-3" />
                    {rp.suburb.postcode}
                  </div>
                  <div className="font-semibold text-gray-900 group-hover:text-[#007A7A] transition-colors">
                    {service.label} in {rp.suburb.name}
                  </div>
                  <div className="mt-2 text-sm text-[#007A7A] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    View page <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[#000000] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold">
            Ready to Start {service.label} Tutoring in {suburb.name}?
          </h2>
          <p className="mt-4 text-gray-300 max-w-2xl mx-auto">
            Book a free consultation with a specialist on our team. We&apos;ll
            assess your goals, recommend the right tutor, and map out your
            first four weeks.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/get-started">
              <Button className="btn-teal" data-testid={`suburb-cta-bottom-${service.key}-${suburb.slug}`}>
                {service.ctaHeadline} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/become-tutor">
              <Button variant="outline" className="border-gray-600 text-white hover:bg-white/10">
                Apply as a Tutor
              </Button>
            </Link>
          </div>

          {/* Secondary internal links for SEO */}
          <div className="mt-10 text-sm text-gray-400">
            Explore:{' '}
            <Link to="/tutors" className="underline hover:text-white">
              Our Tutor Directory
            </Link>{' '}
            ·{' '}
            <Link to="/become-tutor" className="underline hover:text-white">
              Become a Tutor
            </Link>{' '}
            ·{' '}
            <Link to="/get-started" className="underline hover:text-white">
              Get Started
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActionButtons />
    </div>
  );
};

export default SuburbServicePage;
